import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import useUrl from '~/common/hooks/useUrl';
import Button from '~/common/mui/Buttons/MUIButton/MUIButton';
import Modal from '~/common/mui/Modals/Modal';
import ModalBody from '~/common/mui/Modals/ModalElements/ModalBody';
import ModalCloseButton from '~/common/mui/Modals/ModalElements/ModalCloseButton';
import ModalContent from '~/common/mui/Modals/ModalElements/ModalContent';
import ModalFooter from '~/common/mui/Modals/ModalElements/ModalFooter';
import ModalHeader from '~/common/mui/Modals/ModalElements/ModalHeader';
import { typedApiClient } from '~/common/util/api-client';
import { navigateTo } from '~/common/util/browserNavigation';
import typography from '~/theme/mui/typography';

type ConvertCreditQualifyingButtonProps = {
  onClick?: () => void;
  isDisabled?: boolean;
  buttonSx?: Record<string, unknown>;
};

function useConvertToCreditQualifying() {
  const {
    obfuscatedLoanIdentifier: {
      obfuscatedLenderDatabaseId,
      obfuscatedLoanRecordId,
    },
  } = useUrl();
  const showSnackbar = useSnackbar();

  return useMutation({
    mutationFn: async () => {
      const response = await typedApiClient.v1.vaSaveCreditQualifying(
        obfuscatedLenderDatabaseId,
        obfuscatedLoanRecordId,
      );
      return response.data;
    },
    onSuccess: () => {
      const edgeBaseUrl = globalThis.globalConfig.edge?.baseUrl;
      navigateTo(`${edgeBaseUrl}/#/underwritingLanding`);
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: 'Failed to convert to credit qualifying.',
        duration: 5000,
      });
    },
  });
}

export default function ConvertCreditQualifyingButton({
  isDisabled,
  buttonSx,
}: Readonly<ConvertCreditQualifyingButtonProps>) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate, isLoading } = useConvertToCreditQualifying();

  const handleConvert = () => {
    mutate();
    setIsModalOpen(false);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={() => setIsModalOpen(true)}
        sx={buttonSx}
        disabled={isDisabled}
      >
        Convert to Credit Qualifying
      </Button>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="md"
        containerSx={{ top: '280px', transform: 'translateX(-50%)' }}
      >
        <ModalContent>
          <ModalCloseButton onClick={() => setIsModalOpen(false)} />
          <ModalHeader
            sx={{
              px: 6,
              pt: 4,
              pb: 1,
              ...typography.h2_5,
            }}
          >
            Convert to Credit Qualifying?
          </ModalHeader>
          <ModalBody sx={{ mt: '1rem' }}>
            This action will unzap this loan and close BOLT. Loan must be sent
            to a credit designated underwriter to be manually underwritten in
            EDGE.
          </ModalBody>
          <ModalFooter
            sx={{ gap: '1rem', px: 6, py: 4, justifyContent: 'flex-end' }}
          >
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              isLoading={isLoading}
              onClick={handleConvert}
            >
              Convert to Credit Qualifying
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
